"use client";

import React, { useState } from "react";

import { Container } from "./Container";

import {
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
} from "@heroicons/react/24/solid";

export const Products = (props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = props.data.images || [];
  const previewImages = images
    .map((image, index) => ({ image, index }))
    .filter((item) => item.index !== currentIndex)
    .slice(0, 2);

  const nextSlide = () => {
    if (images.length < 2) {
      return;
    }

    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const prevSlide = () => {
    if (images.length < 2) {
      return;
    }

    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + images.length) % images.length
    );
  };

  return (
    <Container className="flex flex-wrap mb-20 md:gap-10 lg:flex-nowrap ">
      {/* CAROUSEL */}
  
        <div
          className={`w-full max-w-3xl mx-auto lg:w-1/2 ${
            props.imgPos === "right" ? "lg:order-1" : ""
          }`}
        >
          <div className="relative rounded-[36px] bg-gradient-to-br from-[#f8fcf8] via-white to-[#eef7ef] p-4 lg:rounded-[48px] lg:p-5">
            <div className="relative lg:grid lg:grid-cols-[minmax(0,1.55fr)_minmax(220px,0.9fr)] lg:items-start lg:gap-4">
            <div className="overflow-hidden relative h-96 rounded-[32px] bg-white lg:h-[34rem] lg:rounded-[42px]">
              {Array.isArray(images) &&
                images.length > 0 &&
                images.map((image, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-transform transform ease-in-out ${
                      index === currentIndex
                        ? "translate-x-0"
                        : "translate-x-full"
                    }`}
                    >
                      <img
                        src={image}
                        alt={`Slide ${index}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/30 via-transparent to-transparent" />
                  </div>
                ))}
            </div>

            <div className="hidden self-start flex-col gap-4 lg:flex">
              {previewImages.map((item) => (
                <button
                  key={item.index}
                  type="button"
                  onClick={() => setCurrentIndex(item.index)}
                  className="group overflow-hidden rounded-[28px] bg-white text-left transition-transform duration-200 hover:-translate-y-0.5 focus:outline-none"
                >
                  <div className="relative h-40 overflow-hidden rounded-[28px]">
                    <img
                      src={item.image}
                      alt={`${props.data.title} preview ${item.index + 1}`}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/25 via-transparent to-transparent" />
                  </div>
                </button>
              ))}
            </div>
            </div>

            {images.length > 1 && (
              <>
                <button
                  className="absolute top-1/2 left-3 -translate-y-1/2 rounded-full bg-white/85 p-2 text-[#2f7d32] lg:hidden"
                  onClick={prevSlide}
                >
                  <ArrowLeftCircleIcon className="size-8" />
                </button>
                <button
                  className="absolute top-1/2 right-3 -translate-y-1/2 rounded-full bg-white/85 p-2 text-[#2f7d32] lg:hidden"
                  onClick={nextSlide}
                >
                  <ArrowRightCircleIcon className="size-8" />
                </button>

                <div className="mt-4 flex items-center justify-center gap-2 lg:hidden">
                  {images.map((image, index) => (
                    <button
                      key={`${image}-${index}`}
                      type="button"
                      onClick={() => setCurrentIndex(index)}
                      aria-label={`Go to slide ${index + 1}`}
                      className={`h-2.5 rounded-full transition-all duration-200 ${
                        index === currentIndex ? "w-8 bg-[#43A047]" : "w-2.5 bg-[#43A047]/25"
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>


      {/* Text */}
      <div
        className={`flex flex-wrap items-center p-8 w-full lg:w-1/2 ${
          props.data.imgPos === "right" ? "lg:justify-end" : ""
        }`}
      >
        <div>
          <div className="flex flex-col w-full mt-4">
            <h3 className="max-w-2xl mt-3 text-3xl font-bold leading-snug tracking-tight text-gray-800 lg:leading-tight lg:text-4xl dark:text-white uppercase">
              {props.data.title}
            </h3>

            <p className="max-w-2xl py-4 text-lg leading-normal text-gray-500 lg:text-xl xl:text-xl dark:text-gray-300 white-space: pre-wrap">
              {props.data.desc}
              <br></br>
              {props.data.desc2}
            </p>
            <p className="max-w-2xl py-4 text-lg leading-normal text-gray-500 lg:text-xl xl:text-xl dark:text-gray-300 white-space: pre-wrap">
              {props.data.desc3}
            </p>
          </div>

          <div
            className={`w-full mt-5 ${
              props.data.bullet === false ? "hidden" : ""
            }`}
          >
            {props.data.bullets.map((item, index) => (
              <Benefit key={index} title={item.title} icon={item.icon}>
                {item.desc}
              </Benefit>
            ))}
          </div>
        </div>
      </div>
    </Container>
  );
};

function Benefit(props) {
  return (
    <div className="flex items-start mt-8 space-x-3">
      <div className="flex items-center justify-center flex-shrink-0 mt-1 bg-green-400 rounded-md w-11 h-11 ">
        {React.cloneElement(props.icon, {
          className: "w-7 h-7 text-green-50",
        })}
      </div>
      <div>
        <h4 className="text-xl font-medium text-gray-800 dark:text-gray-200">
          {props.title}
        </h4>
        <p className="mt-1 text-gray-500 dark:text-gray-400">
          {props.children}
        </p>
      </div>
    </div>
  );
}
